import React, { useEffect, useRef, useState } from "react";
import Icon, { Image } from "./Image";
import { HorizontalCenter } from "../css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

interface TextProps {
  text: string;
  className?: string;
}

export const H1: React.FC<TextProps> = ({ text, className = "" }) => (
  <h1 className={`text-2xl font-bold !leading-normal text-text-default ${className}`}>{text}</h1>
);
export const ContentHead: React.FC<TextProps> = ({ text, className = "" }) => (
  <h1 className={`text-base font-bold !leading-normal text-text-default ${className}`}>{text}</h1>
);

export const H2: React.FC<TextProps> = ({ text, className = "" }) => (
  <h2 className={`font-bold !leading-normal text-sm md:text-base text-text-default ${className}`}>{text}</h2>
);

export const H3: React.FC<TextProps> = ({ text, className = "" }) => (
  <h2 className={`font-bold !leading-normal text-13 text-text-default  ${className}`}>{text}</h2>
);
export const Title: React.FC<TextProps> = ({ text, className = "" }) => {
  const textRef = useRef<HTMLHeadingElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isTextOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsOverflowing(isTextOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text]);

  const titleElement = (
    <h2 ref={textRef} className={`font-bold leading-normal text-13 text-brand-darkBlue truncate ${className}`}>
      {text}
    </h2>
  );

  return isOverflowing ? (
    <Tippy content={text} placement="top" theme="light">
      {titleElement}
    </Tippy>
  ) : (
    titleElement
  );
};

export const LightText: React.FC<TextProps> = ({ text, className = "" }) => (
  <span className={`text-text-light2 font-normal !leading-normal text-13 ${className}`}>{text}</span>
);
export const LightTextWithToolTip: React.FC<TextProps> = ({ text, className = "" }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isTextOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsOverflowing(isTextOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text]);

  const textElement = (
    <span ref={textRef} className={`text-text-default font-normal !leading-normal text-13 truncate ${className}`}>
      {text}
    </span>
  );

  return isOverflowing ? (
    <Tippy content={text} placement="bottom-start" theme="light">
      {textElement}
    </Tippy>
  ) : (
    textElement
  );
};
export const LightTextnormal: React.FC<TextProps> = ({ text, className = "" }) => (
  <span className={`text-text-default text-13 !leading-[12px] ${className}`}>{text}</span>
);
export const LightText2: React.FC<TextProps> = ({ text, className = "" }) => (
  <span className={`text-text-default font-normal leading-normal text-13 ${className}`}>{text}</span>
);
export const LightText3: React.FC<TextProps> = ({ text, className = "" }) => (
  <span className={`text-text-label font-normal !leading-5 text-13 ${className}`}>{text}</span>
);
export const DefaultText: React.FC<TextProps> = ({ text, className = "" }) => (
  <span className={`text-icon-default2 font-bold !leading-normal text-13 ${className}`}>{text}</span>
);
export const LightDarkText: React.FC<TextProps> = ({ text, className = "" }) => (
  <span className={` text-brand-dark2 font-normal !leading-normal text-13 ${className}`}>{text}</span>
);

export const LightP: React.FC<TextProps> = ({ text, className = "" }) => (
  <p className={`text-text-light2 font-normal !leading-normal text-13 ${className}`}>{text}</p>
);
export const LightP2: React.FC<TextProps> = ({ text, className = "" }) => (
  <p className={`text-text-default font-normal !leading-normal text-13 ${className}`}>{text}</p>
);
export const P: React.FC<TextProps> = ({ text, className = "" }) => <p className={`text-base !leading-normal text-text-default ${className}`}>{text}</p>;

export const FieldErrorMessage: React.FC<TextProps> = ({ text, className = "" }) => (
  <p className={`w-full mt-1 text-xs text-text-error !leading-normal ${className}`}>{text}</p>
);

export const InfoText: React.FC<TextProps> = ({ text, className = "" }) => (
  <HorizontalCenter className={`p-2 bg-warningBg rounded gap-x-1 ${className}`}>
    <Image src="/icons/warning-icon.svg" alt="warning-icon" />
    <p className={`text-text-default font-normal !leading-normal text-10 md:text-13 `}>{text}</p>
  </HorizontalCenter>
);

interface IconTextProps extends TextProps {
  iconSrc: string;
  iconClassName?: string;
  textClassName?: string;
}
export const IconText: React.FC<IconTextProps> = ({ text, className = "", iconSrc, iconClassName = "", textClassName = "text-13" }) => (
  <HorizontalCenter className={` gap-x-0.5 ${className}`}>
    <Icon src={iconSrc} alt="warning-icon" className={`${iconClassName}`} />
    <span className={`text-text-default font-normal !leading-normal ${textClassName} `}>{text}</span>
  </HorizontalCenter>
);
